import dayjs from 'dayjs';
import { InboundProcess, OutboundProcess } from '@features/supplyChain/types';
import { SupplyChainProcess, isOutbound } from './processStepModel';

interface DeliveryRow {
  value: string;
  label: string;
}

const dash = '—';

const formatZoned = (
  value?: { dateTime: string; timeZone: string } | null
): string =>
  value
    ? dayjs.utc(value.dateTime).tz(value.timeZone).format('DD MMM YYYY')
    : dash;

function outboundRows(process: OutboundProcess): DeliveryRow[] {
  const { despatchNote, shipmentNote } = process;
  const eta = shipmentNote?.estimatedTimeOfArrival
    ? formatZoned(shipmentNote.estimatedTimeOfArrival)
    : formatZoned(despatchNote?.requiredTimeOfArrival);
  return [
    {
      value: despatchNote?.customer?.name ?? despatchNote?.customerName ?? dash,
      label: 'Customer',
    },
    {
      value: despatchNote?.warehouse?.address ?? dash,
      label: 'Pick-up address',
    },
    {
      value: despatchNote?.customer?.fullAddress ?? dash,
      label: 'Delivery address',
    },
    { value: eta, label: 'ETA' },
    {
      value: despatchNote?.customer?.contactName ?? dash,
      label: 'Contact name',
    },
    {
      value: despatchNote?.customer?.contactNumber ?? dash,
      label: 'Contact number',
    },
    {
      value: despatchNote?.customer?.contactEmail ?? dash,
      label: 'Email address',
    },
  ];
}

function inboundRows(process: InboundProcess): DeliveryRow[] {
  const { shipmentNote } = process;
  return [
    { value: shipmentNote?.supplier?.name ?? dash, label: 'Supplier' },
    {
      value: shipmentNote?.supplier?.fullAddress ?? dash,
      label: 'Pick-up address',
    },
    {
      value:
        shipmentNote?.warehouse?.address ??
        shipmentNote?.warehouseAddress ??
        dash,
      label: 'Delivery address',
    },
    { value: formatZoned(shipmentNote?.estimatedTimeOfArrival), label: 'ETA' },
    {
      value: shipmentNote?.supplier?.contactName ?? dash,
      label: 'Contact name',
    },
    {
      value: shipmentNote?.supplier?.contactNumber ?? dash,
      label: 'Contact number',
    },
    {
      value: shipmentNote?.supplier?.contactEmail ?? dash,
      label: 'Email address',
    },
  ];
}

export function DeliveryDetails({ process }: { process: SupplyChainProcess }) {
  const rows = (
    isOutbound(process) ? outboundRows(process) : inboundRows(process)
  ).filter((row) => row.value !== dash);

  if (rows.length === 0) return null;

  return (
    <div className='bg-gray-50 rounded-lg border border-gray-200 p-4 mb-5'>
      <p className='text-[11px] font-medium text-gray-400 uppercase tracking-wider mb-3'>
        Delivery Details
      </p>
      <div className='grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3'>
        {rows.map((row) => (
          <div key={row.label}>
            <p
              className={`text-sm break-words ${
                row.value === dash
                  ? 'text-gray-400 font-medium'
                  : 'text-gray-900 font-semibold'
              }`}
            >
              {row.value}
            </p>
            <p className='text-xs text-gray-400'>{row.label}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
